import { useRouter } from 'expo-router';
import { useAtomValue } from 'jotai';

import {
  isMemberView,
  supportThreadCounterpartAvatarUrl,
  supportThreadCounterpartName,
} from './participantLabel';

import type { ChatHeaderProfileProps } from '@/components/chat/ChatHeaderContent';
import { useSupportThread } from '@/hooks/conversations/useSupportThread';
import { userIdAtom } from '@/services/globalState';

/**
 * The props the inquiry chat header takes, filled in for a support thread, so
 * the two conversations draw one header: the other party's avatar, their name
 * in the subject slot as a support inquiry does, and the support badge.
 *
 * A member's counterpart is Primum Support, which has no profile, so the header
 * opens nothing for them. Staff open the member's profile.
 */
export function useSupportThreadHeaderProps(conversationId: string): ChatHeaderProfileProps {
  const { navigate } = useRouter();

  const viewerId = useAtomValue(userIdAtom);

  const { data } = useSupportThread(conversationId, { isFocused: false });

  const subject = data?.meta.subject;

  const name = data ? supportThreadCounterpartName(subject, viewerId) : '';

  const avatarUrl = supportThreadCounterpartAvatarUrl(subject, viewerId);

  const memberId = subject?.id;

  const canOpenProfile = !isMemberView(subject, viewerId) && memberId !== undefined;

  return {
    conversationId,
    recipient: {
      id: memberId ?? 0,
      avatar: avatarUrl ? { Location128px: avatarUrl } : undefined,
    },
    displayName: name,
    displaySubject: name,
    onViewProfile: () => {
      if (canOpenProfile) {
        navigate(`/profile/${memberId}`);
      }
    },
    showSupportBadge: true,
  };
}
